import { ReactNode } from "react";
import { Box, Typography } from "@mui/material";

export interface DetailItem {
  label: string;
  value: ReactNode;
}

/**
 * Label/value pairs for detail panels. Replaces the table-as-layout
 * pattern the case views used to rely on, so the markup describes
 * a description list rather than tabular data.
 */
export const DetailList = ({ items }: { items: DetailItem[] }) => (
  <Box component="dl" sx={{ m: 0, display: "grid", gap: 0 }}>
    {items.map((item, index) => (
      <Box
        key={item.label}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "minmax(120px, 40%) 1fr" },
          gap: { xs: 0.25, sm: 2 },
          alignItems: "center",
          py: 1.25,
          borderTop: (theme) =>
            index === 0 ? "none" : `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          component="dt"
          variant="body2"
          sx={{ color: "text.secondary" }}
        >
          {item.label}
        </Typography>
        <Box
          component="dd"
          sx={{
            m: 0,
            fontSize: "0.875rem",
            fontWeight: 500,
            minWidth: 0,
          }}
        >
          {item.value}
        </Box>
      </Box>
    ))}
  </Box>
);

/** Bordered panel with an optional title and trailing action. */
export const DetailPanel = ({
  title,
  action,
  children,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <Box
    sx={{
      height: "100%",
      border: (theme) => `1px solid ${theme.palette.divider}`,
      borderRadius: 3,
      backgroundColor: "background.paper",
      overflow: "hidden",
    }}
  >
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {title && (
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
            {title}
          </Typography>
        )}
        {action && <Box sx={{ ml: "auto" }}>{action}</Box>}
      </Box>
    )}
    <Box sx={{ px: 2, py: 0.5 }}>{children}</Box>
  </Box>
);

export default DetailList;
